import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AimOutlined, ThunderboltOutlined, EyeOutlined,
  CloudOutlined, CalendarOutlined, FireOutlined,
  ArrowUpOutlined, ArrowRightOutlined, ReloadOutlined,
  BellOutlined, BulbOutlined, ShopOutlined,
} from '@ant-design/icons';
import { opportunityApi } from '../../api/opportunity';
import SignalRadar from '../../components/opportunity/SignalRadar';
import ScenarioCards from '../../components/opportunity/ScenarioCards';
import OpportunityList from '../../components/opportunity/OpportunityList';
import TrendChart from '../../components/opportunity/TrendChart';

/* =========================================================
   机会雷达 · 首页
   AI 场景驱动智能选品 — 感知外部世界 → 发现商业机会
   ========================================================= */

export default function OpportunityRadar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [signals, setSignals] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [trend, setTrend] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [scanAnimation, setScanAnimation] = useState(false);
  const containerRef = useRef(null);

  // 数据加载
  const loadDashboard = useCallback(async () => {
    try {
      const res = await opportunityApi.getDashboardStats();
      if (res.code === 200 || res.data) {
        setDashboard(res.data || res);
      }
    } catch (err) {
      console.error('加载仪表盘失败:', err);
      // 使用模拟数据
      setDashboard(getMockDashboard());
    }
  }, []);

  const loadSignals = useCallback(async () => {
    try {
      const res = await opportunityApi.getActiveSignals();
      if (res.code === 200 || res.data) {
        setSignals(res.data || res);
      }
    } catch (err) {
      console.error('加载信号失败:', err);
      setSignals(getMockSignals());
    }
  }, []);

  const loadScenarios = useCallback(async () => {
    try {
      const res = await opportunityApi.getActiveScenarios();
      if (res.code === 200 || res.data) {
        setScenarios(res.data || res);
      }
    } catch (err) {
      console.error('加载场景失败:', err);
      setScenarios(getMockScenarios());
    }
  }, []);

  const loadOpportunities = useCallback(async () => {
    try {
      const res = await opportunityApi.getOpportunityList({ pageSize: 10, minScore: 60 });
      if (res.code === 200 || res.data) {
        setOpportunities(res.data || res);
      }
    } catch (err) {
      console.error('加载机会失败:', err);
      setOpportunities(getMockOpportunities());
    }
  }, []);

  const loadTrend = useCallback(async () => {
    try {
      const res = await opportunityApi.getWeeklyTrend();
      if (res.code === 200 || res.data) {
        setTrend(res.data || res);
      }
    } catch (err) {
      console.error('加载趋势失败:', err);
    }
  }, []);

  // 初始加载
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadDashboard(),
        loadSignals(),
        loadScenarios(),
        loadOpportunities(),
        loadTrend(),
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadSignals, loadScenarios, loadOpportunities, loadTrend]);

  // 刷新
  const handleRefresh = async () => {
    setRefreshing(true);
    setScanAnimation(true);
    await Promise.all([
      loadDashboard(),
      loadSignals(),
      loadScenarios(),
      loadOpportunities(),
    ]);
    setTimeout(() => setScanAnimation(false), 2000);
    setRefreshing(false);
  };

  // 过滤
  const filteredOpportunities = activeTab === 'all'
    ? opportunities
    : opportunities.filter(o => o.scenarioCode === activeTab);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div ref={containerRef} style={{
      minHeight: '100vh',
      /* 透出 #root::before 的森林背景（登录页原图） */
      background: 'transparent',
      padding: '0 0 80px',
    }}>
      {/* ───── 顶部扫描动画 ───── */}
      {scanAnimation && <ScanOverlay />}

      {/* ───── Hero 区域 ───── */}
      <header style={{
        position: 'relative',
        padding: '48px 48px 32px',
        maxWidth: 1280,
        margin: '0 auto',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 24,
        }}>
          {/* 左侧标题 */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 14px', borderRadius: 20,
              background: 'rgba(232,200,90,0.06)',
              border: '1px solid rgba(232,200,90,0.12)',
              fontSize: 12, color: '#E8C85A', fontWeight: 500,
              marginBottom: 16,
            }}>
              <AimOutlined style={{ fontSize: 14 }} />
              AI 商业机会雷达
            </div>

            <h1 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: '-0.04em',
              color: '#F3F5EF',
              margin: '0 0 12px',
              fontFamily: 'var(--font-sans)',
            }}>
              今天发现{' '}
              <span style={{
                background: 'linear-gradient(135deg, #E8C85A 0%, #F5D87A 50%, #E8C85A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {dashboard?.todayOpportunities || 12}
              </span>{' '}
              个商业机会
            </h1>

            <p style={{
              fontSize: 16,
              color: '#64766D',
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 480,
            }}>
              系统感知外部世界变化，识别消费场景，预测需求趋势，
              <br/>为你的生意发现下一个增长窗口。
            </p>
          </div>

          {/* 右侧操作按钮 */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 44, padding: '0 24px',
                borderRadius: 22,
                border: '1px solid rgba(232,200,90,0.2)',
                background: 'rgba(232,200,90,0.05)',
                color: '#E8C85A',
                fontSize: 14, fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 200ms ease',
                opacity: refreshing ? 0.6 : 1,
              }}
              onMouseEnter={e => {
                e.target.style.background = 'rgba(232,200,90,0.1)';
                e.target.style.borderColor = 'rgba(232,200,90,0.3)';
              }}
              onMouseLeave={e => {
                e.target.style.background = 'rgba(232,200,90,0.05)';
                e.target.style.borderColor = 'rgba(232,200,90,0.2)';
              }}
            >
              <ReloadOutlined spin={refreshing} style={{ fontSize: 14 }} />
              {refreshing ? '扫描中...' : '刷新雷达'}
            </button>

            <button
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 44, padding: '0 28px',
                borderRadius: 22,
                border: 'none',
                background: 'linear-gradient(135deg, #E8C85A 0%, #D4B44A 100%)',
                color: '#0a0f0d',
                fontSize: 14, fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 200ms ease',
                boxShadow: '0 4px 16px rgba(232,200,90,0.2)',
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 24px rgba(232,200,90,0.3)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(232,200,90,0.2)';
              }}
            >
              <BellOutlined style={{ fontSize: 14 }} />
              开启通知
            </button>
          </div>
        </div>

        {/* ───── 统计卡片 ───── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginTop: 32,
        }}>
          <StatCard
            label="今日机会"
            value={dashboard?.todayOpportunities || 12}
            change={dashboard?.growthFromYesterday || 4}
            unit="个"
            color="#E8C85A"
            icon={<AimOutlined />}
          />
          <StatCard
            label="活跃场景"
            value={dashboard?.activeScenarios || 4}
            unit="个"
            color="#69B89A"
            icon={<BulbOutlined />}
          />
          <StatCard
            label="信号源"
            value={dashboard?.activeSignals || 6}
            unit="路"
            color="#8EB9D6"
            icon={<ThunderboltOutlined />}
          />
          <StatCard
            label="平均指数"
            value={dashboard?.avgScore || 82}
            unit=""
            color="#A68BD9"
            icon={<FireOutlined />}
          />
        </div>
      </header>

      {/* ───── 主内容区 ───── */}
      <main style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '0 48px',
      }}>
        {/* 信号雷达可视化 */}
        <section style={{ marginBottom: 48 }}>
          <SignalRadar signals={signals} compact />
        </section>

        {/* 场景卡片 */}
        <section style={{ marginBottom: 48 }}>
          <SectionHeader
            title="当前场景"
            icon={<EyeOutlined style={{ color: '#69B89A' }} />}
            extra={<span style={{ fontSize: 13, color: '#64766D' }}>实时感知中</span>}
          />
          <ScenarioCards scenarios={scenarios} />
        </section>

        {/* 趋势图 */}
        <section style={{ marginBottom: 48 }}>
          <SectionHeader
            title="趋势变化"
            icon={<ArrowUpOutlined style={{ color: '#E8C85A' }} />}
            extra={<span style={{ fontSize: 13, color: '#64766D' }}>近7天</span>}
          />
          <TrendChart data={trend} />
        </section>

        {/* 高潜机会列表 */}
        <section>
          <SectionHeader
            title="高潜机会"
            icon={<ShopOutlined style={{ color: '#A68BD9' }} />}
            extra={
              <div style={{ display: 'flex', gap: 8 }}>
                {['all', 'SUMMER_OUTDOOR', 'HEAT_RELIEF', 'HEAVY_RAIN_PREP'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      height: 32, padding: '0 16px',
                      borderRadius: 16,
                      border: activeTab === tab
                        ? '1px solid rgba(232,200,90,0.3)'
                        : '1px solid rgba(255,255,255,0.06)',
                      background: activeTab === tab
                        ? 'rgba(232,200,90,0.08)'
                        : 'transparent',
                      color: activeTab === tab ? '#E8C85A' : '#64766D',
                      fontSize: 13, fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 200ms ease',
                    }}
                  >
                    {tab === 'all' ? '全部' : tab === 'SUMMER_OUTDOOR' ? '暑期' : tab === 'HEAT_RELIEF' ? '高温' : '暴雨'}
                  </button>
                ))}
              </div>
            }
          />
          <OpportunityList opportunities={filteredOpportunities} />
        </section>
      </main>
    </div>
  );
}

/* ───── 子组件 ───── */

function StatCard({ label, value, change, unit, color, icon }) {
  return (
    <div style={{
      padding: '20px 22px',
      borderRadius: 16,
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.05)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 200ms ease',
      cursor: 'default',
    }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      {/* 微光效果 */}
      <div style={{
        position: 'absolute', top: -20, right: -20,
        width: 60, height: 60, borderRadius: '50%',
        background: `${color}08`,
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: '#64766D', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 16, color }}>{icon}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{
          fontSize: 28, fontWeight: 600, color: '#F3F5EF',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
        }}>
          {value}
        </span>
        <span style={{ fontSize: 14, color: '#64766D' }}>{unit}</span>
        {change !== undefined && change > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 2,
            fontSize: 12, color: '#69B89A', fontWeight: 500,
            marginLeft: 'auto',
          }}>
            <ArrowUpOutlined style={{ fontSize: 10 }} />
            {change}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, icon, extra }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <h2 style={{
          fontSize: 18, fontWeight: 600, color: '#F3F5EF',
          margin: 0, letterSpacing: '-0.02em',
        }}>
          {title}
        </h2>
      </div>
      {extra}
    </div>
  );
}

function ScanOverlay() {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      pointerEvents: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 300, height: 300, borderRadius: '50%',
        border: '2px solid rgba(232,200,90,0.3)',
        animation: 'radar-ping 2s ease-out forwards',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,200,90,0.05) 0%, transparent 70%)',
          animation: 'radar-rotate 3s linear infinite',
        }} />
      </div>
      <style>{`
        @keyframes radar-ping {
          0% { transform: scale(0.3); opacity: 1; }
          100% { transform: scale(3); opacity: 0; }
        }
        @keyframes radar-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      /* 透出 #root::before 的森林背景（登录页原图） */
      background: 'transparent',
      gap: 24,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        border: '2px solid rgba(232,200,90,0.1)',
        borderTopColor: '#E8C85A',
        animation: 'spin 1s linear infinite',
      }} />
      <span style={{ fontSize: 14, color: '#64766D' }}>雷达扫描中...</span>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ───── 模拟数据 ───── */

function getMockDashboard() {
  return {
    todayOpportunities: 12,
    growthFromYesterday: 4,
    activeScenarios: 4,
    activeSignals: 6,
    highPotentialCount: 5,
    avgScore: 82,
    weeklyTrend: [
      { date: '08-08', count: 8, avgScore: 75 },
      { date: '08-09', count: 10, avgScore: 78 },
      { date: '08-10', count: 7, avgScore: 72 },
      { date: '08-11', count: 11, avgScore: 80 },
      { date: '08-12', count: 9, avgScore: 77 },
      { date: '08-13', count: 14, avgScore: 85 },
      { date: '08-14', count: 12, avgScore: 82 },
    ],
  };
}

function getMockSignals() {
  return [
    { id: 1, signalType: 'WEATHER', subType: 'HEAT_WAVE', name: '高温热浪', intensity: 85, region: '华东', city: '杭州', status: 'ACTIVE' },
    { id: 2, signalType: 'WEATHER', subType: 'HEAVY_RAIN', name: '暴雨预警', intensity: 72, region: '华南', city: '广州', status: 'ACTIVE' },
    { id: 3, signalType: 'HOLIDAY', subType: 'SUMMER_VACATION', name: '暑假', intensity: 90, region: '全国', city: '全国', status: 'ACTIVE' },
    { id: 4, signalType: 'EVENT', subType: 'CONCERT', name: '演唱会', intensity: 68, region: '华东', city: '杭州', status: 'ACTIVE' },
    { id: 5, signalType: 'CALENDAR', subType: 'WEEKEND', name: '周末效应', intensity: 55, region: '全国', city: '全国', status: 'ACTIVE' },
    { id: 6, signalType: 'SEARCH_TREND', subType: 'PRODUCT_SURGE', name: '搜索上涨', intensity: 78, region: '全国', city: '全国', status: 'ACTIVE' },
  ];
}

function getMockScenarios() {
  return [
    { id: 1, scenarioCode: 'SUMMER_OUTDOOR', name: '暑期户外消费', icon: '🏖️', expectedGrowth: 42, confidence: 88, affectedCategories: '防晒用品,户外装备,便携风扇' },
    { id: 2, scenarioCode: 'HEAVY_RAIN_PREP', name: '暴雨应对需求', icon: '🌧️', expectedGrowth: 55, confidence: 82, affectedCategories: '雨伞,防水鞋套,雨衣' },
    { id: 3, scenarioCode: 'CONCERT_ECONOMY', name: '演唱会周边消费', icon: '🎤', expectedGrowth: 38, confidence: 75, affectedCategories: '应援用品,荧光棒,便携座椅' },
    { id: 4, scenarioCode: 'HEAT_RELIEF', name: '高温降温消费', icon: '☀️', expectedGrowth: 47, confidence: 91, affectedCategories: '防晒霜,冰袖,便携风扇,降温贴' },
  ];
}

function getMockOpportunities() {
  return [
    { id: 1, title: '防晒霜上架机会', scenarioName: '暑期户外消费', score: 92, expectedGrowth: 47, confidence: 91, reason: '暑假+高温+搜索量上涨三重叠加', recommendedProducts: ['防晒霜SPF50', '防晒喷雾', '晒后修复', '冰袖', '遮阳帽'] },
    { id: 2, title: '便携风扇推荐', scenarioName: '暑期户外消费', score: 87, expectedGrowth: 38, confidence: 85, reason: '高温天气下便携小风扇需求急剧上升', recommendedProducts: ['USB便携风扇', '挂颈风扇', '迷你手持风扇'] },
    { id: 3, title: '防晒冰袖爆款机会', scenarioName: '高温降温消费', score: 85, expectedGrowth: 52, confidence: 86, reason: '高温+暑假+搜索上涨，防晒冰袖需求窗口期5-8天', recommendedProducts: ['UV防晒冰袖', '运动冰袖', '骑行袖套'] },
    { id: 4, title: '雨伞及雨具强推', scenarioName: '暴雨应对需求', score: 79, expectedGrowth: 82, confidence: 88, reason: '暴雨橙色预警，雨伞需求预计上涨82%', recommendedProducts: ['折叠伞', '长柄伞', '防水鞋套', '防水手机袋'] },
    { id: 5, title: '冷饮降温场景', scenarioName: '高温降温消费', score: 76, expectedGrowth: 41, confidence: 80, reason: '连续7天35°C以上高温，冰品降温需求为刚需', recommendedProducts: ['冰棍雪糕', '冰镇饮料', '降温贴'] },
    { id: 6, title: '演唱会应援用品', scenarioName: '演唱会周边消费', score: 74, expectedGrowth: 35, confidence: 72, reason: '周末体育场演唱会42000人场次', recommendedProducts: ['荧光棒', '发光手环', '应援手幅'] },
  ];
}
